import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { assignInquiryRecord, describeAssignRecordError } from '../../services/manualInquiryService';
import LeadRecordPicker from './LeadRecordPicker';
import {
  appModalActionsSx,
  appModalCloseButtonSx,
  appModalHeaderDividerSx,
  appModalPaperSx,
  appModalTitleBarSx,
  appModalTitleSx,
} from '../../Components/appModalChrome.styles';
import { LEADS_TEXT_SECONDARY, leadsAlertSx, leadsGhostButtonSx, leadsPrimaryButtonSx } from './leadsUi.styles';

/**
 * «Asignar registro» a una consulta manual abierta (PATCH
 * /assigninquiryrecord). Un solo registro, con el mismo buscador que la carga.
 * `onDone({ ok, refresh, message })`: ok → recargar el lead; error con
 * `refresh` (ya no existe / ya tenía registro) → recargar y avisar.
 *
 * @param {{ inquiry: object|null, ownerUserId?: string|null, onClose: () => void,
 *   onDone: (result: { ok: boolean, refresh?: boolean, message?: string }) => void }} props
 */
export default function LeadAssignRecordDialog({ inquiry, ownerUserId = null, onClose, onDone }) {
  const open = Boolean(inquiry);
  const [records, setRecords] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const abortRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setRecords([]);
    setError('');
  }, [open, inquiry?.id]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (saving) return;
    if (!records.length) {
      setError('Elegí un registro.');
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setSaving(true);
    setError('');
    try {
      const res = await assignInquiryRecord(
        { inquiryId: inquiry.id, record: records[0] },
        { ownerUserId, signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      if (res?._httpOk) {
        onDone({ ok: true });
        return;
      }
      const info = describeAssignRecordError(res);
      if (info.refresh) {
        onDone({ ok: false, refresh: true, message: info.message });
        return;
      }
      setError(info.message);
    } catch (err) {
      if (err?.name !== 'AbortError') setError('No se pudo asignar el registro.');
    } finally {
      if (abortRef.current === controller) setSaving(false);
    }
  };

  const message = String(inquiry?.message || '').trim();

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth={false}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit,
        noValidate: true,
        sx: { ...appModalPaperSx, width: 'min(480px, 95vw)', maxWidth: '480px', height: 'auto', maxHeight: 'min(90vh, 640px)' },
      }}
      aria-labelledby="lead-assign-record-title"
    >
      <DialogTitle id="lead-assign-record-title" sx={appModalTitleBarSx}>
        <Typography component="h2" sx={appModalTitleSx}>Asignar registro</Typography>
        <IconButton size="small" onClick={onClose} disabled={saving} aria-label="Cerrar" sx={appModalCloseButtonSx}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>
      <Divider sx={appModalHeaderDividerSx} />
      <DialogContent sx={{ px: '20px', pt: 2, pb: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {message && (
          <Typography sx={{ fontSize: '13px', color: LEADS_TEXT_SECONDARY, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {message.length > 160 ? `${message.slice(0, 160)}…` : message}
          </Typography>
        )}
        <LeadRecordPicker
          value={records}
          onChange={(next) => { setRecords(next); setError(''); }}
          max={1}
          ownerUserId={ownerUserId}
          disabled={saving}
          label="Registro"
          hint="Se suma como interés del lead (o reactiva el que ya tenía)."
          autoFocus
        />
        {error && <Alert severity="error" variant="outlined" sx={leadsAlertSx}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={appModalActionsSx}>
        <Button variant="outlined" onClick={onClose} disabled={saving} sx={leadsGhostButtonSx}>Cancelar</Button>
        <Button
          type="submit"
          variant="contained"
          disabled={saving}
          sx={leadsPrimaryButtonSx}
          startIcon={saving ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
        >
          Asignar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
